import {
  Badge,
  Box,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Button,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
  Avatar,
  VStack,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { formatRelativeTime } from '../../utils/formatters';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useUnreadNotificationCountByType, useMyNotifications, markNotificationAsRead } from '../../hooks/useNotifications';
import { NotificationsDropdown } from '../notifications/NotificationsDropdown';
import { useUnreadConversationCount } from '../../hooks/useMessages';

const NavLink = ({ children, to, badgeCount = 0, ...rest }) => {
  return (
    <Link to={to}>
      <Button variant="ghost" size="md" color="gray.300" _hover={{ bg: 'whiteAlpha.200', color: 'white' }} position="relative" {...rest}>
        {children}
        {badgeCount > 0 && (
          <Badge
            position="absolute"
            top="-2px"
            right="-6px"
            colorScheme="red"
            borderRadius="full"
            fontSize="10px"
            minW="18px"
            h="18px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {badgeCount > 9 ? '9+' : badgeCount}
          </Badge>
        )}
      </Button>
    </Link>
  );
};

const MobileNavLink = ({ children, to, badgeCount = 0, onClose }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Box
      as={Link}
      to={to}
      onClick={onClose}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      px={4}
      py={3}
      rounded="lg"
      bg={isActive ? 'orange.50' : 'transparent'}
      color={isActive ? 'orange.600' : 'gray.700'}
      fontWeight={isActive ? 'semibold' : 'normal'}
      _hover={{ bg: isActive ? 'orange.50' : 'gray.50' }}
      fontSize="md"
    >
      {children}
      {badgeCount > 0 && (
        <Badge colorScheme="red" borderRadius="full" fontSize="xs" minW="20px" textAlign="center">
          {badgeCount > 9 ? '9+' : badgeCount}
        </Badge>
      )}
    </Box>
  );
};

export const Navbar = () => {
  const { signOut, currentUser } = useAuth();
  const { userData } = useUser();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const bg = 'gray.900';
  const borderColor = 'gray.800';

  // Get unread notification counts for nav link badges
  const myBountiesBadgeCount = useUnreadNotificationCountByType(currentUser?.uid, ['claim_submitted']);
  const myClaimsBadgeCount = useUnreadNotificationCountByType(currentUser?.uid, ['claim_approved', 'claim_rejected', 'claim_disputed', 'bounty_cancelled']);
  const messagesBadgeCount = useUnreadConversationCount(currentUser?.uid);

  // Get recent unread notifications for profile dropdown
  const { notifications } = useMyNotifications(currentUser?.uid, 3);
  const unreadNotifications = notifications.filter((n) => !n.read);

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      try { await markNotificationAsRead(notification.id); } catch (e) { /* ignore */ }
    }
    navigate(notification.actionUrl);
  };

  const handleSignOut = async () => {
    onClose();
    try {
      await signOut();
      navigate('/login');
      toast({
        title: 'Signed out successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error signing out',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const totalMobileBadge = (myBountiesBadgeCount || 0) + (myClaimsBadgeCount || 0) + (messagesBadgeCount || 0);

  return (
    <>
      <Box
        bg={bg}
        px={4}
        borderBottom={1}
        borderStyle="solid"
        borderColor={borderColor}
        position="sticky"
        top={0}
        zIndex={1000}
      >
        <Flex h="60px" alignItems="center" justifyContent="space-between" maxW="7xl" mx="auto">
          <HStack spacing={{ base: 3, md: 8 }} alignItems="center">
            <Link to="/tasks">
              <Image
                src="/attributed-to-logo-sm.webp"
                alt="attributed.to"
                h="60px"
              />
            </Link>

            {/* Desktop navigation */}
            <HStack as="nav" spacing={1} display={{ base: 'none', md: 'flex' }}>
              <NavLink to="/tasks" id="onboarding-browse">Browse Tasks</NavLink>
              <NavLink to="/tasks/create" id="onboarding-post">Post Task</NavLink>
              <NavLink to="/my-tasks" badgeCount={myBountiesBadgeCount}>My Tasks</NavLink>
              <NavLink to="/researchers">Researchers</NavLink>
              {userData?.verificationStatus === 'verified' && (
                <NavLink to="/my-submissions" badgeCount={myClaimsBadgeCount}>My Submissions</NavLink>
              )}
            </HStack>
          </HStack>

          <Flex alignItems="center" gap={{ base: 1, md: 3 }}>
            <NotificationsDropdown />

            {/* Desktop profile menu */}
            <Menu>
              <MenuButton
                as={Button}
                rounded="full"
                variant="link"
                cursor="pointer"
                minW={0}
                display={{ base: 'none', md: 'flex' }}
                _hover={{ textDecoration: 'none' }}
              >
                <HStack spacing={2} id="onboarding-username">
                  <Avatar
                    size="sm"
                    src={userData?.avatarUrl}
                    name={userData?.firstName || 'User'}
                    bg="orange.400"
                  />
                  <Text
                    fontSize="sm"
                    color="gray.300"
                    pb="3px"
                    sx={{
                      borderBottom: '1px solid transparent',
                      'button:hover &': { borderBottom: '1px solid currentColor' },
                    }}
                  >
                    {userData?.anonymizedUsername || 'User'}
                  </Text>
                  <ChevronDownIcon color="gray.300" />
                </HStack>
              </MenuButton>
              <MenuList minW={{ base: '240px', md: '280px' }} maxW="calc(100vw - 16px)">
                {unreadNotifications.length > 0 && (
                  <>
                    <Box px={3} py={2}>
                      <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
                        New Notifications
                      </Text>
                    </Box>
                    {unreadNotifications.map((n) => (
                      <MenuItem
                        key={n.id}
                        onClick={() => handleNotificationClick(n)}
                        py={2}
                        px={3}
                      >
                        <HStack spacing={2} w="full" align="start">
                          <Box w="6px" h="6px" borderRadius="full" bg="orange.500" flexShrink={0} mt="6px" />
                          <Box flex="1" minW={0}>
                            <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{n.title}</Text>
                            <Text fontSize="xs" color="gray.500" noOfLines={1}>{n.message}</Text>
                            <Text fontSize="xs" color="gray.400">{formatRelativeTime(n.createdAt)}</Text>
                          </Box>
                        </HStack>
                      </MenuItem>
                    ))}
                    <MenuDivider />
                  </>
                )}
                <MenuItem as={Link} to={`/users/${userData?.id}`}>
                  My Profile
                </MenuItem>
                <MenuItem as={Link} to="/messages">
                  Messages{messagesBadgeCount > 0 ? ` (${messagesBadgeCount})` : ''}
                </MenuItem>
                <MenuItem as={Link} to="/notifications">
                  Notifications
                </MenuItem>
                <MenuItem as={Link} to="/payments">
                  Payments
                </MenuItem>
                <MenuItem as={Link} to="/settings">
                  Settings
                </MenuItem>
                <MenuItem as={Link} to="/support">
                  Support
                </MenuItem>
                {userData?.isAdmin && (
                  <>
                    <MenuDivider />
                    <MenuItem as={Link} to="/admin" color="orange.500" fontWeight="semibold">
                      Admin Dashboard
                    </MenuItem>
                  </>
                )}
                <MenuDivider />
                <MenuItem onClick={handleSignOut} color="red.500">
                  Sign Out
                </MenuItem>
              </MenuList>
            </Menu>

            {/* Mobile hamburger */}
            <IconButton
              display={{ base: 'flex', md: 'none' }}
              icon={<HamburgerIcon />}
              variant="ghost"
              color="gray.300"
              _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
              aria-label="Open menu"
              onClick={onOpen}
              size="md"
              position="relative"
            >
              {totalMobileBadge > 0 && (
                <Badge
                  position="absolute"
                  top="-2px"
                  right="-2px"
                  colorScheme="red"
                  borderRadius="full"
                  fontSize="9px"
                  minW="16px"
                  h="16px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {totalMobileBadge > 9 ? '9+' : totalMobileBadge}
                </Badge>
              )}
            </IconButton>
          </Flex>
        </Flex>
      </Box>

      {/* Mobile drawer menu */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth={1} pb={3}>
            <HStack spacing={3}>
              <Avatar
                size="sm"
                src={userData?.avatarUrl}
                name={userData?.firstName || 'User'}
                bg="orange.400"
              />
              <Box minW={0}>
                <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                  {userData?.anonymizedUsername || 'User'}
                </Text>
                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                  {currentUser?.email}
                </Text>
              </Box>
            </HStack>
          </DrawerHeader>

          <DrawerBody px={2} py={4}>
            <VStack align="stretch" spacing={1}>
              <MobileNavLink to="/tasks" onClose={onClose}>Browse Tasks</MobileNavLink>
              <MobileNavLink to="/tasks/create" onClose={onClose}>Post Task</MobileNavLink>
              <MobileNavLink to="/my-tasks" badgeCount={myBountiesBadgeCount} onClose={onClose}>My Tasks</MobileNavLink>
              <MobileNavLink to="/researchers" onClose={onClose}>Researchers</MobileNavLink>
              {userData?.verificationStatus === 'verified' && (
                <MobileNavLink to="/my-submissions" badgeCount={myClaimsBadgeCount} onClose={onClose}>My Submissions</MobileNavLink>
              )}

              <Divider my={2} />

              <MobileNavLink to={`/users/${userData?.id}`} onClose={onClose}>My Profile</MobileNavLink>
              <MobileNavLink to="/messages" badgeCount={messagesBadgeCount} onClose={onClose}>Messages</MobileNavLink>
              <MobileNavLink to="/notifications" onClose={onClose}>Notifications</MobileNavLink>
              <MobileNavLink to="/payments" onClose={onClose}>Payments</MobileNavLink>
              <MobileNavLink to="/settings" onClose={onClose}>Settings</MobileNavLink>
              <MobileNavLink to="/support" onClose={onClose}>Support</MobileNavLink>

              {userData?.isAdmin && (
                <>
                  <Divider my={2} />
                  <MobileNavLink to="/admin" onClose={onClose}>Admin Dashboard</MobileNavLink>
                </>
              )}

              <Divider my={2} />

              <Box
                as="button"
                onClick={handleSignOut}
                display="flex"
                alignItems="center"
                px={4}
                py={3}
                rounded="lg"
                color="red.500"
                _hover={{ bg: 'red.50' }}
                fontSize="md"
                w="full"
                textAlign="left"
              >
                Sign Out
              </Box>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
